/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/**
 * Prefetches and caches remote assets (branding logo, favicon, reference
 * media, captured-photo previews) into IndexedDB so they still render if
 * connectivity drops. Customer-captured/completed video is excluded (see
 * pages/ReviewMedia.jsx) — but reference media may opt into a larger
 * `maxBytes` via prefetchAssets(token, urls, { maxBytes }).
 *
 * Shares its IndexedDB database with offlineDb.js (the 'assets' store).
 */

import offlineDb from './offlineDb';

const MAX_CONCURRENT_FETCHES = 4;
const MAX_ASSET_BYTES = 8 * 1024 * 1024; // default per-asset size ceiling
// Matches captureClient.js's axios timeout convention. AssetSyncOverlay blocks
// the whole capture UI until every prefetch settles, so a single asset whose
// connection stalls (accepted but never responds — a real failure mode on
// flaky mobile networks) must not be allowed to hang forever and strand the
// customer behind that overlay indefinitely.
const FETCH_TIMEOUT_MS = 60000;

// Cached/reference asset URLs (captured media in particular) can be signed
// URLs carrying an auth token/signature in the query string — never log the
// raw URL, only origin+pathname, so a fetch failure can't leak it via the
// browser console (which session-replay/monitoring tools may capture).
const redactUrl = (url) => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return '[unparseable URL]';
  }
};

// Dedupes concurrent prefetch calls for the same URL, per token.
const inFlightByToken = new Map();

const getCachedAssetBlob = async (token, url) => {
  if (!token || !url) return null;
  try {
    const db = await offlineDb.openCaptureDb(token);
    const row = await db.get('assets', url);
    return row?.blob ?? null;
  } catch {
    return null;
  }
};

const cacheOne = async (token, url, maxBytes) => {
  const db = await offlineDb.openCaptureDb(token);
  const existing = await db.get('assets', url).catch(() => null);
  if (existing) return; // already cached for this token/URL

  let tokenMap = inFlightByToken.get(token);
  if (!tokenMap) {
    tokenMap = new Map();
    inFlightByToken.set(token, tokenMap);
  }
  if (tokenMap.has(url)) return tokenMap.get(url);

  const promise = (async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(url, { credentials: 'omit', signal: controller.signal });
      if (!response.ok) {
        console.warn(`[assetCache] skip (HTTP ${response.status}): ${redactUrl(url)}`);
        return;
      }
      const declaredLength = Number(response.headers.get('content-length'));
      if (Number.isFinite(declaredLength) && declaredLength > maxBytes) {
        console.warn(`[assetCache] skip (declared ${declaredLength}B > cap ${maxBytes}B): ${redactUrl(url)}`);
        return;
      }
      const blob = await response.blob();
      if (blob.size === 0 || blob.size > maxBytes) {
        console.warn(`[assetCache] skip (blob ${blob.size}B, cap ${maxBytes}B): ${redactUrl(url)}`);
        return;
      }
      await db.put('assets', { url, blob, mimeType: blob.type, byteSize: blob.size, cachedAt: Date.now() });
    } catch (err) {
      // err?.message is deliberately omitted — some browsers echo the full
      // request URL (query string included) back in network-error messages.
      // A timeout abort surfaces here too (err.name === 'AbortError') and is
      // handled the same as any other failed fetch — skipped, not fatal.
      console.warn(`[assetCache] fetch failed (${err?.name}): ${redactUrl(url)}`);
    } finally {
      clearTimeout(timeoutId);
      tokenMap.delete(url);
    }
  })();
  tokenMap.set(url, promise);
  return promise;
};

const runWithConcurrencyLimit = async (items, worker, limit) => {
  const queue = [...items];
  const lanes = Array.from({ length: Math.min(limit, queue.length) }, async () => {
    while (queue.length > 0) {
      const item = queue.shift();
      await worker(item);
    }
  });
  await Promise.all(lanes);
};

// Fire-and-forget: never throws, since this is a background optimization only.
const prefetchAssets = async (token, urls, { maxBytes = MAX_ASSET_BYTES } = {}) => {
  if (!token) return;
  const uniqueUrls = [...new Set((urls || []).filter(Boolean))];
  if (uniqueUrls.length === 0) return;
  try {
    await runWithConcurrencyLimit(uniqueUrls, (url) => cacheOne(token, url, maxBytes), MAX_CONCURRENT_FETCHES);
  } catch (err) {
    console.warn('[assetCache] prefetchAssets batch failed:', err);
  }
};

export default { prefetchAssets, getCachedAssetBlob };
